import { Button, Col, Form, message, Row, Select } from 'antd';
import type { ReactNode } from 'react';
import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import type {
  ReducedDepartment,
  ReducedUniversity,
  UniversityMemberInviteDto,
} from '../Types/interfaces';
import {
  GetDepartmentsOfUniversity,
  GetUniversities,
  InviteMembersToUniversity,
} from '../helper/apiHelper';
import { validateMultiEmail } from '../Validators/ValidatorEmail';

export const UniversityAddMemberComponent = (): ReactNode => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [universities, setUniversities] = useState<ReducedUniversity[]>([]);
  const [selectedUniversity, setSelectedUniversity] =
    useState<ReducedUniversity>();
  const [departments, setDepartments] = useState<ReducedDepartment[]>([]);
  const [selectedDepartment, setSelectedDepartment] =
    useState<ReducedDepartment>();
  const { t } = useTranslation();

  const getUniverities = useCallback(async () => {
    try {
      const universityResp = await GetUniversities();
      setUniversities(universityResp);
      if (universityResp.length === 1) {
        setSelectedUniversity(universityResp[0]);
        form.setFieldValue('selectUniversity', universityResp[0].id);
      }
    } catch (error) {
      console.log(error);
      messageApi.error(t('univMemeberManComp.fetchUnisError'));
    }
  }, [messageApi, t, form]);

  const getDepartments = useCallback(async () => {
    try {
      if (!selectedUniversity) {
        return;
      }
      const resp = await GetDepartmentsOfUniversity(
        selectedUniversity.id.toString()
      );
      setDepartments(resp);

      if (resp.length === 1) {
        setSelectedDepartment(resp[0]);
        form.setFieldValue('selectDepartment', resp[0].id);
      }
    } catch (error) {
      console.log(error);
      messageApi.error(t('univMemeberManComp.fetchUnisError'));
    }
  }, [selectedUniversity, messageApi, t, form]);

  const clearFormFields = (fields?: string[]): void => {
    form.resetFields(fields);
  };

  const inviteMembersToDepartment = async (): Promise<void> => {
    try {
      if (!selectedUniversity) {
        messageApi.info(t('univMemeberManComp.selUniFirst'));
        return;
      }

      const rectorEmails = form.getFieldValue('rectorEmails');
      const legalDepartmentMemberEmails = form.getFieldValue(
        'legalDepartmentMemberEmails'
      );
      const supervisorEmails = form.getFieldValue('supervisorEmails');

      if (supervisorEmails && !selectedDepartment) {
        messageApi.info(t('univMemeberManComp.selDepFirst'));
        return;
      }

      const departmentId = selectedDepartment?.id ?? 0;

      const universityMemberInviteDto: UniversityMemberInviteDto = {
        departmentId,
        rectorEmails: rectorEmails ? rectorEmails : [],
        legalDepartmentMemberEmails: legalDepartmentMemberEmails
          ? legalDepartmentMemberEmails
          : [],
        supervisorEmails: supervisorEmails ? supervisorEmails : [],
      };

      await InviteMembersToUniversity(
        selectedUniversity.id.toString(),
        universityMemberInviteDto
      );

      clearFormFields(['rectorEmails']);
      clearFormFields(['legalDepartmentMemberEmails']);
      clearFormFields(['supervisorEmails']);
      messageApi.success(t('univMemeberManComp.inviteSuccess'));
    } catch (error) {
      console.log(error);
      message.error(t('univMemeberManComp.inviteError'));
    }
  };

  const onUniversitySelect = (
    _value: unknown,
    option: { label: string; value: number }
  ): void => {
    setSelectedUniversity({
      id: option.value,
      name: option.label,
    });

    setSelectedDepartment(undefined);
    form.setFieldValue('selectDepartment', null);
  };

  const onDepartmentSelect = (
    _value: unknown,
    option: { label: string; value: number }
  ): void => {
    setSelectedDepartment({
      id: option.value,
      name: option.label,
    });
  };

  return (
    <>
      {contextHolder}
      <Form form={form} size="middle">
        <Row>
          <Col span={24}>
            <Form.Item name="selectUniversity">
              <Select
                style={{ minWidth: '100%' }}
                showSearch
                allowClear
                placeholder={t('univMemeberManComp.selUni')}
                onClick={() => getUniverities()}
                options={universities?.map((uni) => ({
                  label: uni.name,
                  value: uni.id,
                }))}
                onSelect={onUniversitySelect}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item
              name="rectorEmails"
              rules={[{ validator: validateMultiEmail }]}
            >
              <Select
                mode="tags"
                style={{ minWidth: '100%' }}
                allowClear
                placeholder={t('univMemeberManComp.typeEmailToInviteRectors')}
                tokenSeparators={[',', ' ']}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item
              name="legalDepartmentMemberEmails"
              rules={[{ validator: validateMultiEmail }]}
            >
              <Select
                mode="tags"
                style={{ minWidth: '100%' }}
                allowClear
                placeholder={t(
                  'univMemeberManComp.typeEmailToInviteLegalDepMem'
                )}
                tokenSeparators={[',', ' ']}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item name="selectDepartment">
              <Select
                style={{ minWidth: '100%' }}
                showSearch
                allowClear
                placeholder={t('univMemeberManComp.selDep')}
                onClick={() => getDepartments()}
                options={departments?.map((dep) => ({
                  label: dep.name,
                  value: dep.id,
                }))}
                onSelect={onDepartmentSelect}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item
              name="supervisorEmails"
              rules={[{ validator: validateMultiEmail }]}
            >
              <Select
                mode="tags"
                style={{ minWidth: '100%' }}
                allowClear
                placeholder={t(
                  'univMemeberManComp.typeEmailToInviteSupervisor'
                )}
                tokenSeparators={[',', ' ']}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item>
              <Button
                type="primary"
                style={{ width: '100%' }}
                onClick={inviteMembersToDepartment}
              >
                {t('univMemeberManComp.invToUni')}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
};
